/**
 * Iframe Context Utilities
 * Core detection and utility functions for iframe handling
 */

export const DEFAULT_IFRAME_SOURCE = "whitelabel_iframe";

export const getIframeSourceParam = (
  isIframe: boolean,
  iframeSource?: string | null,
): { embed_source?: string } =>
  isIframe ? { embed_source: iframeSource || DEFAULT_IFRAME_SOURCE } : {};

export const appendIframeSourceToUrl = <T extends string | null | undefined>(
  url: T,
  isIframe: boolean,
  iframeSource?: string | null,
): T => {
  if (!url || !isIframe) return url;
  if (/[?&]embed_source=/.test(url)) return url; // already present
  const sep = url.includes("?") ? "&" : "?";
  return `${url}${sep}embed_source=${encodeURIComponent(
    iframeSource || DEFAULT_IFRAME_SOURCE,
  )}` as T;
};

export interface IframeContextData {
  isIframe: boolean;
  parentOrigin: string | null;
  referer: string | null;
  canAccessParent: boolean;
  isCrossOrigin: boolean;
  iframeSource: string | null;
}

/**
 * Detect if running in iframe (client-side)
 */
export const detectIframeContext = (): IframeContextData => {
  if (typeof window === "undefined") {
    return {
      isIframe: false,
      parentOrigin: null,
      referer: null,
      canAccessParent: false,
      isCrossOrigin: false,
      iframeSource: null,
    };
  }

  let isIframe = false;
  let parentOrigin: string | null = null;
  let canAccessParent = false;
  let isCrossOrigin = false;

  try {
    isIframe = window.self !== window.top;

    if (isIframe) {
      try {
        parentOrigin = window.parent.location.origin;
        canAccessParent = true;
        isCrossOrigin = parentOrigin !== window.location.origin;
      } catch (e) {
        canAccessParent = false;
        isCrossOrigin = true;
      }
    }
  } catch (e) {
    isIframe = true;
    isCrossOrigin = true;
  }

  const referer = typeof document !== "undefined" ? document.referrer : null;

  // Read the iframe origin marker (`embed_source` query param) from the URL
  let iframeSource: string | null = null;
  try {
    const urlParams = new URLSearchParams(window.location.search);
    iframeSource = urlParams.get("embed_source");
  } catch (e) {
    // ignore parse errors
  }

  return {
    isIframe,
    parentOrigin,
    referer,
    canAccessParent,
    isCrossOrigin,
    iframeSource,
  };
};

/**
 * Store iframe context in sessionStorage for persistence
 */
export const storeIframeContext = (context: IframeContextData): void => {
  if (typeof sessionStorage !== "undefined") {
    try {
      sessionStorage.setItem("iframe_context", JSON.stringify(context));
    } catch (e) {
      console.warn("Failed to store iframe context:", e);
    }
  }
};

/**
 * Retrieve stored iframe context
 */
export const getStoredIframeContext = (): IframeContextData | null => {
  if (typeof sessionStorage !== "undefined") {
    try {
      const stored = sessionStorage.getItem("iframe_context");
      return stored ? JSON.parse(stored) : null;
    } catch (e) {
      return null;
    }
  }
  return null;
};

/**
 * Persist just the iframe source marker into the stored iframe context.
 *
 * Called early on the client (app mount) while the URL still carries the
 * `embed_source` query param, so the value survives client-side navigation inside the
 * iframe. This is needed because when the server detects the iframe context
 * during SSR (`isDetected: true`), the client-side detection thunk — which
 * normally writes sessionStorage — is skipped.
 */
export const persistIframeSource = (source?: string | null): void => {
  if (typeof window === "undefined") return;

  let value = source ?? null;
  if (!value) {
    try {
      value = new URLSearchParams(window.location.search).get("embed_source");
    } catch (e) {
      value = null;
    }
  }
  if (!value) return;

  const existing = getStoredIframeContext() || detectIframeContext();
  storeIframeContext({ ...existing, iframeSource: value });
};

export const getIframeSource = (): string | null => {
  if (typeof window === "undefined") return null;

  // 1. Prefer the live URL param (present on the initial iframe load).
  //    Persist it so later calls (after in-iframe navigation) still find it.
  try {
    const urlParams = new URLSearchParams(window.location.search);
    const fromUrl = urlParams.get("embed_source");
    if (fromUrl) {
      persistIframeSource(fromUrl);
      return fromUrl;
    }
  } catch (e) {
    // ignore parse errors
  }

  // 2. Fall back to the persisted context (survives in-iframe navigation)
  const stored = getStoredIframeContext();
  return stored?.iframeSource || null;
};

/**
 * Navigate in iframe context (opens in parent or new tab)
 */
export const navigateInIframe = (url: string, openInParent = true): void => {
  if (typeof window === "undefined") return;

  const context = detectIframeContext();

  if (!context.isIframe) {
    window.location.href = url;
    return;
  }

  if (openInParent && context.canAccessParent) {
    try {
      window.parent.location.href = url;
    } catch (e) {
      window.open(url, "_blank");
    }
  } else {
    window.open(url, "_blank");
  }
};
